import * as React from 'react';
import { OrgComponentProps } from '../typescriptHelpers';
import FormItem from 'antd/lib/form/FormItem';
import { AppPlatform, CompletedStepsTrigger, AppStoreType } from '../types';
import { useWatch } from 'antd/lib/form/Form';
import { Form, Radio } from 'antd/lib';
import { BlockingStepsSelector } from './BlockingStepsSelector';
import { StepGraphStep } from '../types/workflow';

interface CompletedStepsTriggerSettingsProps extends OrgComponentProps {
    appId: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    stepGraphStepsMap: Record<string, StepGraphStep>;
}

function resolveArchetypesToStepConfigIds(
    trigger: CompletedStepsTrigger,
    stepGraphStepsMap: Record<string, StepGraphStep>,
): string[] {
    if (!trigger.requiredSteps?.length) {
        return [];
    }
    const archetypeSet = new Set(trigger.requiredSteps);
    return Object.values(stepGraphStepsMap)
        .filter((step) => archetypeSet.has(step.archetype))
        .map((step) => step.stepConfigId);
}

const requiredStepConfigIdsValidator = async (_, values) => {
    if (!values?.length) {
        return Promise.reject(
            new Error('Please adjust your selection or select a required release steps to be complete (green).'),
        );
    }
    return Promise.resolve();
};

export const CompletedStepsTriggerSettings: React.FC<CompletedStepsTriggerSettingsProps> = ({
    appId,
    platform,
    appStoreType,
    stepGraphStepsMap,
}: CompletedStepsTriggerSettingsProps) => {
    const form = Form.useFormInstance();
    const trigger = useWatch<CompletedStepsTrigger>('completedStepsTrigger');

    React.useEffect(() => {
        if (!trigger) {
            return;
        }

        const needsLegacyMigration = !trigger.requiredStepConfigIds?.length && !!trigger.requiredSteps?.length;
        if (!needsLegacyMigration) {
            return;
        }

        const migratedStepConfigIds = resolveArchetypesToStepConfigIds(trigger, stepGraphStepsMap);
        form.setFieldValue('completedStepsTrigger', {
            ...trigger,
            requiredStepConfigIds: migratedStepConfigIds,
            requiredSteps: [],
        });
    }, [form, trigger, stepGraphStepsMap]);

    return (
        <div style={{ paddingLeft: 16 }}>
            <FormItem
                name={['completedStepsTrigger', 'hasCustomRequiredSteps']}
                initialValue={false}
                style={{ marginBottom: 0, marginTop: 0 }}
            >
                <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Radio value={false}>Require all previous release steps</Radio>
                    <Radio value={true}>Require only selected release steps</Radio>
                </Radio.Group>
            </FormItem>
            {trigger?.hasCustomRequiredSteps && (
                <FormItem
                    required
                    name={['completedStepsTrigger', 'requiredStepConfigIds']}
                    rules={[{ validator: requiredStepConfigIdsValidator }]}
                    style={{ marginTop: 16, marginBottom: 0 }}
                >
                    <BlockingStepsSelector
                        appId={appId}
                        platform={platform}
                        appStoreType={appStoreType}
                        stepGraphStepsMap={stepGraphStepsMap}
                    />
                </FormItem>
            )}
        </div>
    );
};
